// src/components/quiz/QuizProgressBar.jsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft, SkipForward, Zap, Trophy, Sparkles } from 'lucide-react';

export default function QuizProgressBar({ 
  qIdx, 
  totalQ, 
  streak, 
  sessionXP, 
  onPrev, 
  onNext, 
  feedback 
}) {
  const [justProgressed, setJustProgressed] = useState(false);
  const currentStreak = streak || 0;
  const progressPercentage = ((qIdx + 1) / totalQ) * 100;

  // Animación al cambiar de pregunta
  useEffect(() => {
    setJustProgressed(true);
    const timer = setTimeout(() => setJustProgressed(false), 500);
    return () => clearTimeout(timer);
  }, [qIdx]);

  // Lógica del multiplicador
  let multiplierBadge = null;
  if (currentStreak >= 50) {
    multiplierBadge = (
      <div className="animate-in zoom-in bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 text-yellow-300 text-[10px] font-black px-3 py-1 rounded-full border-2 border-yellow-400 shadow-[0_0_20px_rgba(234,179,8,0.8)] flex items-center gap-1 animate-pulse">
        <Trophy size={12} />
        GOD MODE x3
      </div>
    );
  } else if (currentStreak >= 10) {
    multiplierBadge = (
      <div className="animate-in zoom-in bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-[10px] font-black px-3 py-1 rounded-full border-2 border-cyan-200 shadow-[0_0_15px_rgba(6,182,212,0.8)] animate-pulse flex items-center gap-1">
        <Zap size={12} />
        XP x2
      </div>
    );
  }

  return (
    <div className="space-y-2 animate-in fade-in duration-500">
      {/* CONTADOR DE PREGUNTA Y PORCENTAJE */}
      <div className="flex items-center justify-between text-xs font-bold text-slate-500 px-1">
        <span className="flex items-center gap-1">
          Pregunta <span className="text-indigo-600 text-sm">{qIdx + 1}</span> de {totalQ}
        </span>
        <span className={`transition-all duration-300 ${justProgressed ? 'scale-110 text-indigo-600' : ''}`}>
          {Math.round(progressPercentage)}%
        </span>
      </div>

      {/* BARRA DE PROGRESO PRINCIPAL */}
      <div className="relative">
        <div className="flex items-center gap-3">
          {/* BOTÓN ANTERIOR */}
          <button
            onClick={onPrev}
            disabled={qIdx === 0 || !!feedback}
            className={`p-2.5 md:p-3 rounded-full border-2 transition-all shadow-sm ${
              qIdx === 0 
                ? 'opacity-0 pointer-events-none' 
                : 'bg-white border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-300 hover:scale-110 active:scale-95'
            }`}
          >
            <ChevronLeft size={20} strokeWidth={3} />
          </button>

          {/* BARRA DE PROGRESO */}
          <div className="flex-1 relative">
            {/* Fondo de la barra */}
            <div className="h-4 bg-slate-100 rounded-full overflow-hidden border-2 border-slate-200 shadow-inner relative">
              {/* Barra de progreso con gradiente */}
              <div 
                className={`h-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-purple-600 transition-all duration-700 ease-out relative overflow-hidden ${
                  justProgressed ? 'animate-pulse' : ''
                }`}
                style={{ width: `${progressPercentage}%` }} 
              >
                {/* Efecto de brillo animado */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
              </div>

              {/* Marcadores de progreso (círculos pequeños) */}
              <div className="absolute inset-0 flex items-center justify-between px-1">
                {[...Array(totalQ)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i < qIdx + 1 
                        ? 'bg-white shadow-md scale-110' 
                        : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Indicador de completado al 100% */}
            {progressPercentage === 100 && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 animate-in zoom-in fade-in duration-300">
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-black shadow-lg flex items-center gap-1">
                  <Sparkles size={12} />
                  ¡COMPLETADO!
                </div>
              </div>
            )}
          </div>

          {/* BOTÓN SIGUIENTE */}
          {!feedback ? (
            <button
              onClick={onNext}
              className="p-2.5 md:p-3 rounded-full border-2 bg-white border-slate-200 text-slate-400 hover:text-amber-500 hover:border-amber-300 hover:scale-110 transition-all group active:scale-95 shadow-sm"
            >
              <SkipForward size={20} strokeWidth={3} />
            </button>
          ) : (
            <div className="w-[44px] md:w-[52px]" />
          )}
        </div>
      </div>

      {/* STATS: MULTIPLICADOR, STREAK Y XP */}
      <div className="flex items-center justify-end gap-2 px-1">
        {/* Badge de multiplicador */}
        {multiplierBadge}

        {/* Contador de streak */}
        <div className={`flex items-center gap-1.5 font-black text-xs md:text-sm px-3 py-1.5 rounded-full border-2 transition-all shadow-md ${
          currentStreak >= 50
            ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-600 border-orange-300 scale-110 animate-pulse'
            : currentStreak >= 10 
              ? 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-600 border-orange-300 scale-105' 
              : currentStreak > 0
                ? 'bg-orange-50 text-orange-500 border-orange-200'
                : 'bg-slate-100 text-slate-400 border-slate-200'
        }`}>
          <span className={currentStreak >= 10 ? 'animate-bounce' : ''}>🔥</span>
          <span>{currentStreak}</span>
          
          {/* Partículas de fuego para streaks altos */}
          {currentStreak >= 10 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-ping" />
          )}
        </div>

        {/* Contador de XP */}
        <div className="relative group">
          <div className={`flex items-center gap-1 font-black text-xs px-3 py-1.5 rounded-full border-2 transition-all shadow-md ${
            sessionXP > 0
              ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600 border-indigo-300'
              : 'bg-slate-100 text-slate-400 border-slate-200'
          }`}>
            <Sparkles size={12} className={sessionXP > 0 ? 'text-indigo-500' : 'text-slate-400'} />
            <span>+{sessionXP || 0}</span>
          </div>

          {/* Tooltip en hover */}
          <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl">
              XP de esta sesión
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
            </div>
          </div>
        </div>
      </div>

      {/* ESTILOS ADICIONALES */}
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}
